import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Menu, Tooltip } from 'antd';
import { ItemType } from 'antd/es/menu/interface';
import {
  selectedMenuKeysSelector,
  selectedOpenKeysSelector,
} from 'features/app-info/selectors';
import {
  setSelectedMenuKeys,
  setSelectedOpenKeys,
} from 'features/app-info/slice';
import {
  DOCUMENT_MANAGEMENT_TYPE,
  VOSA_DOCUMENT_TYPE,
} from 'features/documents/types';
import { TASK_MANAGEMENT_TYPE } from 'features/tasks/constants';
import { useAppDispatch, useAppSelector } from 'lib/stores/hook';
import type { MenuInfo } from 'rc-menu/es/interface';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getPath } from 'routes/router-paths';
import { v4 as uuid } from 'uuid';
const menuIcon = (
  <div className="block size-[0.8rem] !min-w-0 rounded-full border border-secondary" />
);

const AppMainNavigationMenu = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();
  const location = useLocation();

  const selectedMenuKeys = useAppSelector(selectedMenuKeysSelector);
  const selectedOpenKeys = useAppSelector(selectedOpenKeysSelector);

  const handleClickMenuItem = useCallback(
    (info: MenuInfo) => {
      const { key: navigationPath } = info;
      dispatch(setSelectedMenuKeys([navigationPath]));
      navigate(navigationPath);
    },
    [dispatch, navigate]
  );

  const renderSidebarLabel = (label: string) => {
    return (
      <Tooltip title={label} placement="right">
        {label}
      </Tooltip>
    );
  };

  const menuItems = useMemo(
    (): ItemType[] => [
      {
        key: getPath('portalPage'),
        label: renderSidebarLabel('Trang chủ'),
      },
      {
        key: 'Công việc',
        label: 'Công việc',
        type: 'group',
        children: [
          {
            key: getPath('MyWorkPage'),
            icon: menuIcon,
            label: renderSidebarLabel('Công việc của tôi'),
          },
          {
            key: uuid(),
            icon: menuIcon,
            label: renderSidebarLabel('CV đang theo dõi'),
          },
          {
            key: uuid(),
            icon: menuIcon,
            label: renderSidebarLabel('KPI'),
          },
        ],
      },
      {
        key: 'Văn bản',
        label: 'Văn bản',
        type: 'group',
        children: [
          {
            key: getPath('VOSADocumentsPage', VOSA_DOCUMENT_TYPE.INCOMING),
            icon: menuIcon,
            label: renderSidebarLabel('Văn bản VOSA'),
          },
          {
            key: getPath(
              'documentManagementPage',
              DOCUMENT_MANAGEMENT_TYPE.INTERNAL
            ),
            icon: menuIcon,
            label: renderSidebarLabel('Quản trị'),
          },
        ],
      },
      {
        key: 'Nhiệm vụ',
        label: 'Nhiệm vụ',
        type: 'group',
        children: [
          {
            key: getPath('TaskListPage'),
            icon: menuIcon,
            label: renderSidebarLabel('Nhiệm vụ'),
          },
          {
            key: getPath('TaskOversightPage'),
            icon: menuIcon,
            label: renderSidebarLabel('Lãnh đạo'),
          },
          {
            key: getPath('TaskManagementPage', TASK_MANAGEMENT_TYPE.GROUP),
            icon: menuIcon,
            label: renderSidebarLabel('Quản trị'),
          },
        ],
      },
      {
        key: 'E-Office',
        label: 'E-Office',
        type: 'group',
        children: [
          {
            key: uuid(),
            icon: menuIcon,
            label: renderSidebarLabel('Sổ điện tử'),
          },
          {
            key: uuid(),
            icon: menuIcon,
            label: renderSidebarLabel('Thông báo'),
          },
          {
            key: uuid(),
            icon: menuIcon,
            label: renderSidebarLabel('Tài liệu ISO'),
          },
          {
            key: uuid(),
            icon: menuIcon,
            label: renderSidebarLabel('Tin nhắn'),
          },
          {
            key: uuid(),
            icon: menuIcon,
            label: renderSidebarLabel('Danh bạ'),
          },
          {
            key: uuid(),
            icon: menuIcon,
            label: renderSidebarLabel('Văn bản pháp chế'),
          },
        ],
      },
      {
        key: 'Hành chính',
        label: 'Hành chính',
        type: 'group',
        children: [
          {
            key: getPath('OfficeCalendarManagementPage'),
            icon: menuIcon,
            label: renderSidebarLabel('Lịch cơ quan'),
          },
          {
            key: uuid(),
            label: renderSidebarLabel('Hành chính'),
            icon: menuIcon,
          },
          {
            key: getPath('PayrollsPage'),
            label: renderSidebarLabel('Bảng lương'),
            icon: menuIcon,
          },
          {
            key: getPath('BodOpinionsPage'),
            label: renderSidebarLabel('Ý kiến HĐQT'),
            icon: menuIcon,
            // className: 'custom-sub-menu-group',
          },
          {
            key: getPath('BodResolutionsPage'),
            label: renderSidebarLabel('Biên bản - NQ'),
            icon: menuIcon,
            // className: 'custom-sub-menu-group',
          },
          {
            key: getPath('BodReportsPage'),
            label: renderSidebarLabel('Báo cáo HĐQT'),
            icon: menuIcon,
            // className: 'custom-sub-menu-group',
          },
        ],
      },
      {
        key: 'Phiếu kiểm soát rủi ro',
        label: 'Phiếu kiểm soát rủi ro',
        type: 'group',
        children: [
          {
            key: uuid(),
            icon: menuIcon,
            label: renderSidebarLabel('Danh sách'),
          },
          {
            key: uuid(),
            label: renderSidebarLabel('Chế độ báo cáo'),
            icon: menuIcon,
          },
          {
            key: uuid(),
            label: renderSidebarLabel('Quản trị rủi ro'),
            icon: menuIcon,
          },
          {
            key: uuid(),
            label: renderSidebarLabel('Quyền xem'),
            icon: menuIcon,
            // className: 'custom-sub-menu-group',
          },
          {
            key: uuid(),
            label: renderSidebarLabel('Quyền tạo báo cáo'),
            icon: menuIcon,
            // className: 'custom-sub-menu-group',
          },
        ],
      },
      {
        key: 'Quản trị',
        label: 'Quản trị',
        type: 'group',
        children: [
          {
            key: getPath('DepartmentManagementPage'),
            icon: menuIcon,
            label: renderSidebarLabel('Phòng ban'),
          },
          {
            key: getPath('PositionManagementPage'),
            label: renderSidebarLabel('Chức danh'),
            icon: menuIcon,
          },
          {
            key: getPath('AccountManagementPage'),
            label: renderSidebarLabel('Tài khoản'),
            icon: menuIcon,
          },
          {
            key: getPath('PermissionManagementPage'),
            label: renderSidebarLabel('Phân quyền'),
            icon: menuIcon,
          },
          {
            key: getPath('UserGroupManagementPage'),
            label: renderSidebarLabel('Nhóm người dùng'),
            icon: menuIcon,
          },
          {
            key: getPath('SignatureAccountManagementPage'),
            label: renderSidebarLabel('Tài khoản ký số'),
            icon: menuIcon,
          },
        ],
      },
    ],
    []
  );

  useEffect(() => {
    dispatch(setSelectedMenuKeys([location.pathname]));
  }, [dispatch, location.pathname]);

  return (
    <Menu
      mode="inline"
      theme="dark"
      items={menuItems}
      onClick={handleClickMenuItem}
      selectedKeys={selectedMenuKeys}
      openKeys={selectedOpenKeys}
      onOpenChange={keys => dispatch(setSelectedOpenKeys(keys))}
      className="!border-r-0"
      expandIcon={({ isOpen }) =>
        isOpen ? (
          <ArrowForwardIosIcon className="rotate-90 !text-xs text-white" />
        ) : (
          <ArrowForwardIosIcon className="!text-xs text-[#00b2ff]" />
        )
      }
    />
  );
};

export default memo(AppMainNavigationMenu);
